import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, LogIn, ArrowRight, Sparkles, Globe } from 'lucide-react';
import * as Icons from 'lucide-react';
import { NAV, MEGA_MENU, SITE } from '../data/mock';
import Logo from './Logo';
import { useI18n, LANGUAGES } from '../context/I18nContext';

function LanguageSwitcher({ solid }) {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${solid ? 'text-slate-700 hover:text-violet-600' : 'text-white hover:text-violet-200'}`}
        aria-label="Change language"
      >
        <Globe size={16} /> {current.flag} <ChevronDown size={12} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-36 rounded-xl bg-white shadow-xl border border-slate-200 py-1 z-50">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onMouseDown={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-violet-50 ${l.code === lang ? 'text-violet-700 font-bold' : 'text-slate-700'}`}
            >
              <span>{l.flag}</span> {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Narrative + legal pages render with a light hero and no dark background
// behind the navbar. On those routes the navbar must behave as if the
// user had already scrolled — otherwise white-on-white renders invisible
// links. Service / home pages keep their dark hero + transparent navbar.
const LIGHT_HERO_ROUTES = new Set([
  '/about',
  '/awards',
  '/reviews',
  '/privacy-policy',
  '/terms-conditions',
  '/cookies-policy',
  '/acceptable-use-policy',
]);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const [annOpen, setAnnOpen] = useState(true);
  const location = useLocation();
  const { t } = useI18n();
  // Map a NAV item's English label to its i18n key (nav.home, nav.services…).
  const navLabel = (label) => t(`nav.${String(label).toLowerCase()}`, label);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMega(null);
  }, [location.pathname]);

  // Force the "scrolled" / solid look on light-hero routes so the navbar
  // is always legible.
  const forceSolid = LIGHT_HERO_ROUTES.has(location.pathname);
  const solid = scrolled || forceSolid;

  const linkColor = solid ? 'text-slate-700 hover:text-violet-600' : 'text-white hover:text-violet-200';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-[0_4px_30px_-12px_rgba(15,23,42,0.12)]'
          : 'bg-transparent'
      }`}
    >
      {annOpen && !solid && (
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white text-sm">
          <div className="container-x flex items-center justify-center gap-3 py-2 relative">
            <Sparkles size={14} className="animate-pulse-slow" />
            <p className="font-medium tracking-wide">
              {t('announce.freeTrial')}
            </p>
            <button
              aria-label="Close announcement"
              onClick={() => setAnnOpen(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div
        className="container-x flex items-center justify-between h-20"
        onMouseLeave={() => setOpenMega(null)}
      >
        <Link to="/" className="flex items-center gap-2" aria-label="VoIPinger home">
          <Logo
            variant={solid ? 'color' : 'white'}
            className="h-9 md:h-10 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.mega ? (
              <div key={item.label} className="relative" onMouseEnter={() => setOpenMega(item.mega)}>
                <button
                  className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1 transition-colors ${linkColor} ${
                    openMega === item.mega ? (solid ? 'text-violet-600' : 'text-violet-200') : ''
                  }`}
                >
                  {navLabel(item.label)} <ChevronDown size={14} className={`transition-transform ${openMega === item.mega ? 'rotate-180' : ''}`} />
                </button>
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.href}
                onMouseEnter={() => setOpenMega(null)}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isActive
                      ? solid ? 'text-violet-600' : 'text-violet-200'
                      : linkColor
                  }`
                }
              >
                {navLabel(item.label)}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneTel}`}
            className={`hidden xl:flex items-center gap-2 text-sm font-semibold transition-colors ${linkColor}`}
          >
            <Phone size={16} className={solid ? 'text-violet-600' : 'text-violet-300'} />
            {SITE.phone}
          </a>
          <LanguageSwitcher solid={solid} />
          <a
            href={SITE.loginUrl}
            target="_blank"
            rel="noreferrer"
            className={`hidden md:inline-flex items-center gap-2 text-sm font-semibold transition-colors ${linkColor}`}
          >
            <LogIn size={16} /> {t('nav.login')}
          </a>
          <Link to="/contact" className="btn-primary text-sm">
            {t('cta.freeTrial')} <ArrowRight size={16} />
          </Link>
        </div>

        <button
          className={`lg:hidden p-2 ${solid ? 'text-slate-700' : 'text-white'}`}
          onClick={() => setMobileOpen((s) => !s)}
          aria-label="Toggle navigation menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* MEGA MENU PANEL */}
      {openMega && MEGA_MENU[openMega] && (
        <div
          className="hidden lg:block absolute left-0 right-0 top-full"
          onMouseEnter={() => setOpenMega(openMega)}
          onMouseLeave={() => setOpenMega(null)}
        >
          <div className="container-x pt-3">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
              <div className="grid lg:grid-cols-12">
                <div className="lg:col-span-3 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 p-7 border-r border-slate-100">
                  <p className="eyebrow mb-3">{MEGA_MENU[openMega].title}</p>
                  <h4 className="text-xl font-bold text-slate-900 leading-tight mb-3">
                    {MEGA_MENU[openMega].sub}
                  </h4>
                  <p className="text-sm text-slate-600">
                    Explore the full {MEGA_MENU[openMega].title.toLowerCase()} portfolio &mdash; built to scale with your business.
                  </p>
                  <Link to="/contact" className="mt-5 inline-flex items-center gap-1 text-violet-600 font-semibold text-sm hover:gap-2 transition-all">
                    Talk to a specialist <ArrowRight size={14} />
                  </Link>
                </div>

                <div className="lg:col-span-9 p-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                  {MEGA_MENU[openMega].columns.map((col) => (
                    <div key={col.heading}>
                      <p className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mb-3">
                        {col.heading}
                      </p>
                      <div className="space-y-1">
                        {col.items.map((it) => {
                          const Ic = Icons[it.icon] || Icons.ChevronRight;
                          return (
                            <NavLink
                              key={it.label}
                              to={it.href}
                              className="group flex items-start gap-3 p-2.5 rounded-xl hover:bg-violet-50 transition-colors"
                            >
                              <span className="w-9 h-9 rounded-lg bg-slate-50 group-hover:bg-white text-violet-600 flex items-center justify-center flex-shrink-0 border border-slate-100">
                                <Ic size={16} />
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm font-semibold text-slate-900 group-hover:text-violet-700">
                                  {it.label}
                                </span>
                                <span className="block text-xs text-slate-500 leading-snug mt-0.5">
                                  {it.desc}
                                </span>
                              </span>
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE MENU */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-white border-t border-slate-100 ${
          mobileOpen ? 'max-h-[85vh] overflow-y-auto' : 'max-h-0'
        }`}
      >
        <div className="container-x py-4 flex flex-col gap-1">
          {NAV.map((item) =>
            item.mega ? (
              <details key={item.label} className="group">
                <summary className="list-none cursor-pointer flex items-center justify-between py-3 text-slate-800 font-semibold">
                  {item.label}
                  <ChevronDown size={16} className="transition-transform group-open:rotate-180" />
                </summary>
                <div className="pl-2 pb-3 space-y-3">
                  {MEGA_MENU[item.mega].columns.flatMap((col) =>
                    col.items.map((it) => (
                      <Link
                        key={it.label}
                        to={it.href}
                        className="flex items-start gap-3 py-2 px-2 rounded-lg hover:bg-violet-50"
                      >
                        <span className="text-violet-600 mt-1">
                          {(() => {
                            const Ic = Icons[it.icon] || Icons.ChevronRight;
                            return <Ic size={14} />;
                          })()}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">{it.label}</p>
                          <p className="text-xs text-slate-500">{it.desc}</p>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </details>
            ) : (
              <Link key={item.label} to={item.href} className="py-3 text-slate-800 font-semibold hover:text-violet-600">
                {item.label}
              </Link>
            )
          )}
          <div className="flex flex-col gap-2 pt-3 border-t border-slate-100 mt-2">
            <a href={`tel:${SITE.phoneTel}`} className="py-2 flex items-center gap-2 text-slate-700 font-semibold">
              <Phone size={16} className="text-violet-600" /> {SITE.phone}
            </a>
            <a href={SITE.loginUrl} target="_blank" rel="noreferrer" className="py-2 flex items-center gap-2 text-slate-700 font-semibold">
              <LogIn size={16} /> Login Portal
            </a>
            <Link to="/contact" className="btn-primary justify-center mt-2">
              Start Free Trial <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
